import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TbChevronLeft, TbDeviceMobile, TbLogin } from 'react-icons/tb';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { PageState } from '../components/PageState';
import { formatDate } from '../lib/format';
import { RoutineSection } from '../admin/routine/RoutineSection';
import { MemberProgressSection } from '../admin/routine/MemberProgressSection';
import { coachApi as defaultCoachApi } from './coach-api';
import './coach.css';

function AttendanceSection({ memberId, api }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', checkins: [], message: '' });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading', checkins: [], message: '' });
    api.listMemberAttendance(memberId)
      .then((checkins) => { if (active) setState({ status: 'ready', checkins: checkins || [], message: '' }); })
      .catch((error) => {
        if (!active) return;
        const outcome = handleApiError(error);
        if (!outcome.handled) setState({ status: 'error', checkins: [], message: errorMessageFor(error) });
      });
    return () => { active = false; };
  }, [api, memberId, handleApiError]);

  return (
    <section className="gym-card gym-coach-attendance" aria-labelledby="coach-attendance-title">
      <h2 id="coach-attendance-title" className="gym-card__title">Asistencia</h2>
      {state.status === 'loading' && <p role="status">Cargando asistencia…</p>}
      {state.status === 'error' && <p role="alert" className="gym-form__error">{state.message}</p>}
      {state.status === 'ready' && (state.checkins.length === 0 ? (
        <p className="gym-muted-text">Todavía no hay asistencias registradas.</p>
      ) : (
        <ul className="gym-coach-attendance__list">
          {state.checkins.map((checkin) => <li key={checkin.id}><span className="gym-coach-attendance__icon" aria-hidden="true">{checkin.source === 'kiosk' ? <TbLogin /> : <TbDeviceMobile />}</span><time dateTime={checkin.date}>{formatDate(checkin.date)}</time><span>{checkin.source === 'kiosk' ? 'Recepción' : 'Aplicación'}</span></li>)}
        </ul>
      ))}
    </section>
  );
}

export function CoachMemberPage(props) {
  const { id } = useParams();
  return <CoachMemberPageForId key={id} id={id} {...props} />;
}

function CoachMemberPageForId({ id, api = defaultCoachApi }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', member: null, message: '' });

  const handleScopedNotFound = useCallback((error) => {
    if (error && error.status === 404) {
      setState({ status: 'error', member: null, message: 'No se encontró el socio.' });
    }
  }, []);

  const scopedApi = useMemo(() => {
    const memberScopedMethods = [
      'listMemberRoutines', 'createRoutine', 'updateRoutine', 'archiveRoutine',
      'restoreSubstitution', 'getMemberProgress', 'getMemberExerciseProgress',
      'listMemberSessions', 'listMemberAttendance',
    ];
    const wrapped = { ...api };
    memberScopedMethods.forEach((method) => {
      if (typeof api[method] !== 'function') return;
      wrapped[method] = (...args) => Promise.resolve()
        .then(() => api[method](...args))
        .catch((error) => {
          handleScopedNotFound(error);
          throw error;
        });
    });
    return wrapped;
  }, [api, handleScopedNotFound]);

  const load = useCallback(() => {
    let active = true;
    setState({ status: 'loading', member: null, message: '' });
    api.getMember(id)
      .then((member) => { if (active) setState({ status: 'ready', member, message: '' }); })
      .catch((error) => {
        if (!active) return;
        const outcome = handleApiError(error, { clearBusinessData: () => setState({ status: 'loading', member: null, message: '' }) });
        if (!outcome.handled) {
          const message = error && error.status === 404 ? 'No se encontró el socio.' : errorMessageFor(error);
          setState({ status: 'error', member: null, message });
        }
      });
    return () => { active = false; };
  }, [api, handleApiError, id]);

  useEffect(() => load(), [load]);

  if (state.status === 'loading') return <PageState title="Ficha del socio" status="loading" />;
  if (state.status === 'error') {
    return (
      <PageState
        title="Ficha del socio"
        status="error"
        message={state.message}
        action={(
          <>
            <button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button>
            <Link className="gym-button gym-button--secondary" to="/coach">Volver a mis socios</Link>
          </>
        )}
      />
    );
  }

  const { member } = state;
  const { user, profile } = member;
  const name = user.username || user.email || 'Socio';
  return (
    <section className="gym-page gym-coach-detail" aria-labelledby="gym-page-title">
      <p className="gym-coach-detail__back"><Link to="/coach"><TbChevronLeft aria-hidden="true" focusable="false" /><span className="gym-sr-only">← </span>Mis socios</Link></p>
      <header className="gym-page__header gym-coach-detail__header">
        <div>
          <h1 id="gym-page-title" className="gym-page__title">{name}</h1>
          <p className="gym-coach-detail__email">{user.email}</p>
        </div>
        <div className="gym-coach-detail__stats">
          <div className="gym-coach-detail__goal">
            <span>Objetivo</span><strong>{profile?.fitnessGoal || 'Sin definir'}</strong>
          </div>
          <div className="gym-coach-detail__goal gym-coach-detail__frequency">
            <span>Frecuencia</span>
            <strong>{profile?.targetDaysPerWeek != null ? `${profile.targetDaysPerWeek} días por semana` : 'Sin objetivo definido'}</strong>
          </div>
        </div>
      </header>

      <section className="gym-coach-detail__section" aria-labelledby="coach-routine-title">
        <h2 id="coach-routine-title" className="gym-coach-section-title">Plan de entrenamiento</h2>
        <RoutineSection memberId={id} api={scopedApi} />
      </section>
      <section className="gym-coach-detail__section" aria-labelledby="coach-progress-title">
        <h2 id="coach-progress-title" className="gym-coach-section-title">Seguimiento</h2>
        <MemberProgressSection memberId={id} api={scopedApi} />
      </section>
      <AttendanceSection memberId={id} api={scopedApi} />
    </section>
  );
}
